import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { Loader2, Lock, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { getPaymentsConfig } from '../services/api';

let stripePromise = null;
const getStripe = async () => {
  if (!stripePromise) {
    const { data } = await getPaymentsConfig();
    if (!data?.publishable_key) throw new Error('Payments are not configured');
    stripePromise = loadStripe(data.publishable_key);
  }
  return stripePromise;
};

/** Stripe Embedded Checkout rendered inside JustDeliver — no redirect away. */
export default function StripeCheckoutDialog({ open, clientSecret, onClose }) {
  const [stripe, setStripe] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let active = true;
    getStripe()
      .then((p) => { if (active) setStripe(p); })
      .catch(() => { if (active) setError('Payments are temporarily unavailable. Please try again shortly.'); });
    return () => { active = false; };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="max-w-xl w-[calc(100vw-1.5rem)] sm:w-full max-h-[92vh] overflow-y-auto overflow-x-hidden p-0 gap-0"
        data-testid="stripe-checkout-dialog"
      >
        <DialogHeader className="px-5 py-4 border-b border-gray-100 text-left sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <img
              src="/jd-mark.png"
              alt="JustDeliver.ie"
              className="h-8 w-auto flex-shrink-0"
              data-testid="stripe-checkout-logo"
            />
            <div className="min-w-0">
              <DialogTitle className="text-base flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#2E7D32]" /> Secure payment
              </DialogTitle>
              <p className="text-xs text-gray-500 mt-0.5">
                Card details are handled by Stripe. JustDeliver never sees your card number.
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="relative px-3 py-3 sm:px-5 sm:py-4 min-h-[320px]" data-testid="stripe-checkout-mount">
          {/* Faint JD watermark behind the payment form */}
          <img
            src="/jd-mark.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none select-none absolute right-4 bottom-4 w-28 sm:w-36 opacity-[0.06]"
          />
          {error ? (
            <div className="py-10 text-center">
              <p className="text-sm text-red-600" data-testid="stripe-checkout-error">{error}</p>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-gray-900"
                data-testid="stripe-checkout-error-close"
              >
                <X className="w-4 h-4" /> Close
              </button>
            </div>
          ) : stripe && clientSecret ? (
            <div className="relative z-[1]">
              <EmbeddedCheckoutProvider stripe={stripe} options={{ clientSecret }}>
                <EmbeddedCheckout className="w-full" />
              </EmbeddedCheckoutProvider>
            </div>
          ) : (
            <div className="relative z-[1] flex flex-col items-center justify-center py-14 gap-3" data-testid="stripe-checkout-loading">
              <img src="/jd-mark.png" alt="JustDeliver.ie" className="h-10 w-auto opacity-90" />
              <Loader2 className="w-6 h-6 text-[#2E7D32] animate-spin" />
              <p className="text-sm text-gray-500">Opening secure payment…</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
